/**
 * Config-driven PostHog event filter for `before_send`.
 *
 * Evaluation order per event:
 *   1. Not in config? Internal ($) → DROP. Custom → check '*'. No match → SEND (100%)
 *   2. enabled: false → DROP
 *   3. URL matches an exclude pattern → DROP
 *   4. weekdays set, today not in list → DROP
 *   5. paths set, URL matches a rule → use that rule's sample rate
 *   6. No path match / no paths → use fallback sample rate
 *   7. No sample defined → SEND (100%)
 */

/**
 * @typedef {Object} PathRule
 * @property {string} match - Substring to match against pathname
 * @property {number} sample - Sample rate for this path (0–1)
 * @property {boolean} [exact] - If true, requires exact pathname match instead of substring
 */

/**
 * @typedef {Object} EventFilterRule
 * @property {boolean} enabled - Whether this event type is enabled at all
 * @property {number} [sample] - Fallback sample rate (0–1); omit to send 100%
 * @property {string[]} [exclude] - Pathnames that always drop the event
 * @property {number[]} [weekdays] - Days to capture (0=Sun..6=Sat); omit=all
 * @property {PathRule[]} [paths] - Path-specific sample rates; first match wins
 */

/**
 * Default filter configuration.
 *
 * Internal ($) events: default-deny — unlisted ones are dropped automatically.
 * Custom events: fall through to the '*' wildcard entry.
 *
 * Edit the rules below to change sampling behavior.
 *
 * @type {Object.<string, EventFilterRule>}
 */
export const EVENT_FILTER_CONFIG = {
  $identify: {
    enabled: false,
  },
  $feature_flag_called: {
    enabled: false,
  },
  $rageclick: {
    enabled: false,
  },
  $$heatmap: {
    enabled: false,
  },
  $web_vitals: {
    enabled: false,
  },
  $pageleave: {
    enabled: false,
  },
  $pageview: {
    enabled: false,
  },
  $set: {
    enabled: false,
  },
  $autocapture: {
    enabled: false,
  },
  '*': {
    enabled: true,
    sample: 0,
    paths: [
      { match: '/cupons/', sample: 0.5 },
      { match: '/oferta/', sample: 0.5 },
      { match: '/', sample: 0.5, exact: true },
    ],
  },
};

/**
 * Extracts pathname from a URL string.
 * Returns '/' on any parse error.
 *
 * @param {string} url
 * @returns {string}
 */
const getPathname = (url) => {
  try {
    return new URL(url).pathname;
  } catch {
    return '/';
  }
};

/**
 * Returns true if pathname matches pattern.
 * When exact is true, requires full pathname equality; otherwise substring match.
 *
 * @param {string} pathname
 * @param {string} pattern
 * @param {boolean} [exact]
 * @returns {boolean}
 */
const matchesPattern = (pathname, pattern, exact = false) =>
  exact ? pathname === pattern : pathname.includes(pattern);

/**
 * Returns true if the event should be kept given the sample rate.
 *
 * @param {number} rate - Value between 0 and 1 (inclusive)
 * @returns {boolean}
 */
const shouldSample = (rate) => {
  if (rate >= 1) return true;
  if (rate <= 0) return false;
  return Math.random() < rate;
};

/**
 * Factory that returns a `before_send` callback wired to the given config.
 *
 * @param {Object.<string, EventFilterRule>} config
 * @returns {(captureResult: object) => object|null}
 */
export const createBeforeSend = (config) => (captureResult) => {
  const { event, properties } = captureResult;

  // Resolve the rule: exact match first, then '*' only for non-$ events
  const isInternal = event.startsWith('$');
  let rule = config[event];

  if (rule === undefined) {
    if (isInternal) {
      // Default-deny for unlisted internal events (quota protection)
      return null;
    }
    rule = config['*'];
    // No wildcard configured → send at 100%
    if (rule === undefined) return captureResult;
  }

  // Step 2: enabled check
  if (!rule.enabled) return null;

  const url = properties?.$current_url ?? '';
  const pathname = getPathname(url);

  // Step 3: exclude list
  if (rule.exclude) {
    for (const pattern of rule.exclude) {
      if (matchesPattern(pathname, pattern)) return null;
    }
  }

  // Step 4: weekday restriction
  if (rule.weekdays) {
    const today = new Date().getDay();
    if (!rule.weekdays.includes(today)) return null;
  }

  // Step 5: path-specific sample rate (first match wins)
  if (rule.paths) {
    for (const pathRule of rule.paths) {
      if (matchesPattern(pathname, pathRule.match, pathRule.exact)) {
        return shouldSample(pathRule.sample) ? captureResult : null;
      }
    }
  }

  // Step 6: fallback sample rate
  if (rule.sample !== undefined) {
    return shouldSample(rule.sample) ? captureResult : null;
  }

  // Step 7: no sample defined → send 100%
  return captureResult;
};
